import {
   Attribute,
  Component, ElementRef,
  Injector,
  Optional, ViewChild
} from '@angular/core';
import { CommonModule } from "@angular/common";

import { DISPLAY_TYPE, IWidgetConfig, StylableComponent, provideAsWidgetRef, Parent, register, styler } from "@wm/components-base";
import { FormsModule } from "@angular/forms";
import { isDefined, runtimeEventManager, toggleClass } from "@wm/core";
import { set } from "lodash-es";


const DEFAULT_CLS = 'app-checkbox checkbox';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-checkbox',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};

/*
 * try to convert the chekedvalue and unchecked values to boolean/number
 */
export const unStringify = (val: any, defaultVal?: any) => {
  if (val === null) {
    return defaultVal;
  }

  if (val === true || val === 'true') {
    return true;
  }

  if (val === false || val === 'false') {
    return false;
  }

  const number = parseInt(val, 10);
  if (!isNaN(number)) {
    return number;
  }
  return val;
};

@Component({
  selector: '[wmCheckbox]',
  template: `<label
    [ngClass]="{'unchecked': !proxyModel, 'disabled': (disabled || readonly), 'required': (required && _caption), 'icon-button-toggle': type === 'button'}"
    role="button">
    <input type="checkbox"
           role="checkbox"
           #checkbox
           [attr.name]="name"
           [attr.aria-label]="hint || (type === 'toggle' ? 'Toggle' : 'Checkbox')"
           [attr.aria-checked]="!!proxyModel"
           focus-target
           [(ngModel)]="proxyModel"
           [readonly]="readonly"
           [required]="required"
           [disabled]="disabled || readonly"
           [attr.accesskey]="shortcutkey"
           [tabindex]="tabindex">
    <span class="caption" [innerHtml]="_caption" *ngIf="type != 'button' && type != 'toggle'"></span>
    <span class="caption fa-xs text-center" *ngIf="type == 'toggle'">
        <span name="unselectedIcon" class="start-icon" [ngClass]="unselectedicon" *ngIf="!proxyModel"></span>
        <span name="selectedIcon" class="end-icon" [ngClass]="selectedicon" *ngIf="proxyModel"></span>
    </span>
    <span class="toggle-icon" [ngClass]="unselectedicon" *ngIf="type === 'button' && !proxyModel"></span>
    <span class="toggle-icon" *ngIf="proxyModel && type === 'button'" [ngClass]="selectedicon"></span>
    <img alt="Checkbox Image" src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
         class="switch"/>
  </label>
  <input type="hidden" class="ng-hide model-holder" aria-hidden="true" tabindex="-1" [disabled]="disabled"
         [value]="proxyModel">`,
  providers: [
    provideAsWidgetRef(CheckboxComponent)
  ],
  exportAs: 'wmCheckbox',
  standalone: true,
  imports: [CommonModule, FormsModule],
  styles: []
})
export class CheckboxComponent extends StylableComponent {
  static initializeProps = register('wm-checkbox');
  static initializeToggleProps = register('wm-toggle');
  static initializeIconButtonToggleProps = register('wm-iconbutton-toggle');
  
  public proxyModel: any;
  public disabled: boolean;
  public readonly: boolean;
  public required: boolean;
  public name: string;
  public hint: string;
  public shortcutkey: string;
  public tabindex: any;
  public _caption = '&nbsp';
  private _checkedvalue: any;
  private _uncheckedvalue: any;
  private _datavalue: any;
  private isNativeMobileApplicationType: boolean;
  public unselectedicon: string;
  public selectedicon: string;

  @ViewChild('checkbox', { static: true, read: ElementRef }) checkboxEl: ElementRef;

  constructor(inj: Injector,
              @Attribute('checkedvalue')checkedVal:string,
              @Attribute('uncheckedvalue')uncheckedVal:string,
              @Attribute('type') public type:string,
              @Optional() viewParent: Parent) {
    if (type === 'button') {
      WIDGET_CONFIG.hostClass = 'app-iconbutton-toggle'
    }else {
      WIDGET_CONFIG.hostClass = DEFAULT_CLS
    }
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    this._checkedvalue = unStringify(checkedVal, true);
    this._uncheckedvalue = unStringify(uncheckedVal, false);
    toggleClass(this.nativeElement, 'app-toggle', type === 'toggle');
    this.isNativeMobileApplicationType = runtimeEventManager.getService('projectService').isNativeMobileApplicationType();
    // Show animation properties only for toggle widgets
    this.toggleAnimationProperties(type === 'toggle');
  }

  private toggleAnimationProperties(isToggle: boolean) {
    const animationProps = ['handlesizeon', 'handlesizeoff', 'handlesizebounce', 'bounceduration', 'slideduration'];
    animationProps.forEach(prop => {
      if (this.widgetProps && this.widgetProps[prop]) {
        set(this.widgetProps, `${prop}.show`, isToggle && this.isNativeMobileApplicationType);
      }
    });
  }

  override get stylerEl(): HTMLElement {
    return this.nativeElement.querySelector('.app-toggle > label') || this.nativeElement;
  }

  get datavalue(): any {
    return isDefined(this.proxyModel) ? (this.proxyModel ? this._checkedvalue : this._uncheckedvalue) : undefined;;
  }

  set datavalue(v: any) {
    this._datavalue = v;
    this.proxyModel = (isDefined(v) && v !== '') ? v === this._checkedvalue : undefined;
  }

  public override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'datavalue') {
      this.datavalue = unStringify(nv);
    } else if (key === 'checkedvalue') {
      this._checkedvalue = unStringify(nv, true);
      this.datavalue = this._datavalue;
    } else if (key === 'uncheckedvalue') {
      this._uncheckedvalue = unStringify(nv, false);
      this.datavalue = this._datavalue;
    } else if (key === 'caption') {
      if (!isDefined(nv) || nv === '') {
        this._caption = '&nbsp;';
      } else {
        this._caption = nv;
      }
    } else if (key === 'unselectedicon') {
      this.unselectedicon = nv
    } else if (key === 'selectedicon') {
      this.selectedicon = nv
    }  else {
      super.onPropertyChange(key, nv, ov);
    }
  }

}

